import { useCallback, useEffect, useState } from "react";
import {
  Outlet,
  useLocation,
  useNavigate,
  useOutletContext,
} from "react-router-dom";
import PageLayout from "../../../../../components/PageLayout";
import ToggleTabs from "../../../../../components/ToggleTabs";
import { useDebounce } from "../../../../../hooks/use-debounce";
import { ERA_EOB_SUB_TABS } from "../billing-constant";
import { EraEobFilterData } from "./EraEobFilters";
import EraEobSummaryCards, { EraEobSummaryCounts } from "./EraEobSummaryCards";

type ParentOutletContext = {
  filter: EraEobFilterData;
  setFilter: (v: EraEobFilterData) => void;
  searchKey: string;
};

export type EraEobChildContext = {
  filter: EraEobFilterData;
  debouncedSearchKey: string;
  onSummaryCountsChange: (counts: EraEobSummaryCounts) => void;
};

const EraEobTab = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { filter, searchKey } = useOutletContext<ParentOutletContext>();

  const activeSubTab = (() => {
    const segments = location.pathname.split("/").filter(Boolean);
    const last = segments[segments.length - 1];
    return last === "eob" ? "eob" : "era";
  })();

  // useDebounce seeds its state from the argument once, so passing `searchKey`
  // straight in froze the debounced value at "" and the worklists never saw a
  // search. Feed every change through the hook's dispatcher instead.
  const [debouncedSearchKey, setDebouncedSearchKey] = useDebounce<string>("");

  useEffect(() => {
    setDebouncedSearchKey(searchKey ?? "");
  }, [searchKey, setDebouncedSearchKey]);

  const [summaryCounts, setSummaryCounts] = useState<EraEobSummaryCounts>({
    numberOfERAs: 0,
    numberOfEOBs: 0,
    numberOfMappedEOBs: 0,
    numberOfUnmappedEOBs: 0,
    numberOfSuccessfulPayments: 0,
    numberOfPaymentFailures: 0,
  });

  const [selectedSummaryCard, setSelectedSummaryCard] = useState<string | null>(
    null
  );

  const handleSummaryCardClick = (cardKey: string) => {
    setSelectedSummaryCard((prev) => (prev === cardKey ? null : cardKey));
  };

  const handleSubTabChange = (newValue: string) => {
    navigate(newValue);
  };

  const handleSummaryCountsChange = useCallback(
    (counts: EraEobSummaryCounts) => {
      setSummaryCounts(counts);
    },
    []
  );

  const childContext: EraEobChildContext = {
    filter,
    debouncedSearchKey,
    onSummaryCountsChange: handleSummaryCountsChange,
  };

  return (
    <>
      <PageLayout
        header={
          <>
            <EraEobSummaryCards
              counts={summaryCounts}
              onCardClick={handleSummaryCardClick}
              activeCard={selectedSummaryCard}
            />
            <ToggleTabs
              tabs={ERA_EOB_SUB_TABS}
              activeTab={activeSubTab}
              onTabChange={handleSubTabChange}
              containerSx={{
                borderBottom: "none",
                px: 0,
                py: 0,
                "& .MuiTypography-root": { fontWeight: "400 !important" },
              }}
            />
          </>
        }
      >
        <Outlet context={childContext} />
      </PageLayout>
    </>
  );
};

export default EraEobTab;
